import logging from '@sstdev/lib_logging';
import { DEPLOYMENT_SLOT_SUFFIXES } from './deploymentSlotSuffixes';

const EMQX_REWRITES = [
    ['.sstid.com', '-emqx.sstid.com'],
    ['.redbeam.com', '-emqx.sstid.com'],
    // Not "redirect to the local docker-compose emqx service" - a native device's own
    // "localhost" refers to itself, not the dev machine, so it can't reach a broker bound to
    // the dev machine's loopback interface anyway. This instead points at the always-on shared
    // dev broker. Reaching the local docker-compose emqx container (silo/config/docker-compose.yml)
    // from a device needs the optional dev-certs + `adb reverse` workflow instead - see
    // silo/config/compose/optional/emqx-dev-certs.yml.
    ['localhost', 'dev-emqx.sstid.com']
];

// Matches a deployment slot suffix on the leading hostname label only, e.g. the "-green" in
// "app-green.sstid.com". There is one EMQX broker per base environment, not per slot (see
// getBaseEnvironment.js in lib_k8s-client), so the slot has to
// come off before the rewrites above can find the right broker host. No "i" flag - the hostname
// is lower-cased before this is applied (see below), so it doesn't need one, and EMQX_REWRITES'
// own matching below is case-sensitive regardless.
const DEPLOYMENT_SLOT_SUFFIX = new RegExp(`^([^.]+)-(?:${DEPLOYMENT_SLOT_SUFFIXES.join('|')})(?=\\.|$)`);

/**
 * The EMQX MQTT broker is deployed as its own service, never on the app's own hostname
 * (see the dedicated `emqx` service in silo/config/docker-compose.yml, and the
 * namespace-local EMQX Service for deployed environments) - so the app's hostname
 * needs this rewrite before it can be used as the broker host, or connections silently
 * time out against a host with nothing listening on the MQTT port.
 *
 * Used directly by willChange_rulesEngine_customerWorkflow.js (lib_ui-core) for rules-engine
 * forward actions, rather than duplicated there - see that file's history (#1701) for what
 * happened the last time this logic existed in two places.
 */
export default function getMqttBrokerHostname(hostname) {
    if (!hostname) {
        logging.warn('[getMqttBrokerHostname] No hostname provided - cannot derive an MQTT broker host.');
        return hostname;
    }
    // Lower-cased once up front: EMQX_REWRITES is matched with String#includes and applied with
    // String#replace below, both case-sensitive, and real callers only ever pass a lower-case
    // location.hostname/__PROD_DOMAIN_NODE__ - but an upper-case input should still resolve
    // correctly rather than silently falling through to the no-match branch below.
    const normalizedHostname = hostname.toLowerCase();
    const baseHostname = normalizedHostname.replace(DEPLOYMENT_SLOT_SUFFIX, '$1');
    const rewrite = EMQX_REWRITES.find(([match]) => baseHostname.includes(match));
    if (!rewrite) {
        // e.g. a debug native build's __DEBUG_HOSTNAME__, which is a bare LAN IP (see
        // getHostname.native.js / getPreferredIp()) - matches none of the patterns above, so it
        // passes through unchanged. That's the same silent-timeout failure mode this function
        // exists to fix, just one hop earlier. logging.warn is invisible at `adb logcat *:E`,
        // which is what people actually capture in the field when chasing an unattributed 30s
        // MQTT connect timeout - error so it survives that filter (#1701 follow-up).
        logging.error(`[getMqttBrokerHostname] Hostname "${hostname}" matched no known EMQX rewrite - using it unchanged as the broker host.`);
        return hostname;
    }
    const [match, replacement] = rewrite;
    return baseHostname.replace(match, replacement);
}
